/**
 * 공개 화면 셸 — 로그인·가입·메일 인증·승인 대기. 랜딩(livegpt.co.kr) 히어로의 디자인 언어를 가져왔다
 * (260917-admin-area-split). 넓은 화면은 좌우 분할(왼쪽 히어로 고정), 좁은 화면은 히어로가 위로 올라간다.
 *
 * 문구 규칙(260916-brand-livegpt): 존댓말 · 용어는 "질문" · 서비스명(BRAND)과 사업자(COMPANY)를 섞지 않는다.
 * 넣지 않는 것: 우리가 측정하지 않는 엔진 이름(지금은 ChatGPT 만 잰다), 각주가 필요한 고객 수.
 */
import type { ReactNode } from 'react';
import { SHELL } from '../lib/branding.ts';
import { ChartIcon, CheckIcon, MessageIcon, ReportIcon } from './icons.tsx';
import { LogoMark } from './LogoMark.tsx';

export function AuthShell({
  aside,
  width = 'md',
  children,
}: {
  aside: ReactNode;
  width?: 'md' | 'xl';
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-slate-50 font-suit lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      {aside}
      <main className="flex justify-center px-4 py-8 lg:items-start lg:px-10 lg:py-16">
        <div className={`w-full space-y-4 motion-safe:animate-rise ${width === 'xl' ? 'max-w-xl' : 'max-w-md'}`}>
          {children}
        </div>
      </main>
    </div>
  );
}

export function HeroPanel({
  eyebrow,
  title,
  lead,
  children,
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <aside className="relative overflow-hidden rounded-b-3xl bg-hero text-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:self-start lg:overflow-y-auto lg:rounded-none">
      <div className="flex flex-1 flex-col gap-8 px-5 pt-6 pb-8 sm:px-8 lg:px-12 lg:py-10">
        {/* 링크를 걸지 않는다 — 작성 중인 신청서·승인 대기 화면에서 다른 곳으로 빠지지 않게 */}
        <span className="self-start">
          <LogoMark tone="onDark" />
        </span>
        <div className="space-y-4 lg:my-auto">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-semibold text-[13px] text-blue-100 ring-1 ring-white/15">
            {eyebrow}
          </span>
          <h1 className="text-balance break-keep font-extrabold text-[28px] leading-[1.22] tracking-tight sm:text-4xl lg:text-[42px]">
            {title}
          </h1>
          {lead ? <p className="max-w-md break-keep text-[#aebbd0] text-[15px] leading-relaxed">{lead}</p> : null}
          {children}
        </div>
        <TrustList />
      </div>
    </aside>
  );
}

/** 강조 글씨 — 랜딩 히어로의 그라데이션 */
export const Em = ({ children }: { children: ReactNode }) => <span className="text-hero-em">{children}</span>;

/**
 * 가입 절차 — 고객이 "신청하면 바로 쓰는 것"으로 오해하지 않게, 승인·협의가 사이에 있다는 것을 **폼보다 먼저**
 * 보여준다 (260916-client-self-signup). 좁은 화면에서도 히어로 안(폼 위)에 있다.
 */
export const SIGNUP_STEPS = [
  { label: '신청서 작성', note: '필요한 정보를 남겨 주세요' },
  { label: '검토 후 연락', note: '담당자가 확인하고 연락드립니다' },
  { label: '질문·비용 협의', note: '노출할 질문과 비용을 정하고 계약합니다' },
  { label: '매일 리포트', note: '매일 갱신되는 측정 기록을 보여 드립니다' },
] as const;

export function JourneySteps({ current }: { current: number }) {
  return (
    <div className="pt-2">
      <ol className="relative grid grid-cols-4 gap-1">
        {/* 단계를 잇는 선 — 첫·마지막 원의 중심까지만 */}
        <span
          aria-hidden
          className="absolute top-[18px] right-[12.5%] left-[12.5%] h-0.5 bg-gradient-to-r from-white/15 to-[#6ea2ff]"
        />
        {SIGNUP_STEPS.map((s, i) => {
          const done = i < current;
          const now = i === current;
          return (
            <li key={s.label} className="relative flex flex-col items-center gap-2 text-center">
              <span
                className={`flex h-9 w-9 items-center justify-center rounded-full border-2 font-extrabold text-sm ${
                  now
                    ? 'border-[#8fb4ff] bg-live text-white shadow-[0_0_0_6px_#2563eb47,0_0_34px_#60a5faa6]'
                    : done
                      ? 'border-white/40 bg-white text-night'
                      : 'border-white/25 bg-[#0b1a30] text-[#9fb2cc]'
                }`}
              >
                {done ? <CheckIcon className="h-4 w-4" strokeWidth={3} /> : i + 1}
              </span>
              <span
                className={`break-keep font-semibold text-[12px] leading-tight ${now ? 'text-[#9cc0ff]' : 'text-[#aebbd0]'}`}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="mt-4 break-keep text-center text-[#aebbd0] text-[13px]">{SIGNUP_STEPS[current]?.note}</p>
    </div>
  );
}

const VALUES = [
  { icon: MessageIcon, title: '질문 설계', text: '고객이 실제로 AI 에게 물을 질문을 함께 정합니다' },
  {
    icon: ChartIcon,
    title: 'AI 검색 최적화(GEO)',
    text: 'AI가 우리 브랜드를 답변할 수 있도록 최적화합니다',
  },
  { icon: ReportIcon, title: '리포트', text: '매일 갱신되는 측정 기록으로 보여 드립니다' },
] as const;

export function ValueList() {
  return (
    <ul className="hidden gap-4 pt-4 lg:grid">
      {VALUES.map((v) => (
        <li key={v.title} className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#9cc0ff] ring-1 ring-white/15">
            <v.icon className="h-[18px] w-[18px]" />
          </span>
          <span>
            <span className="block font-bold text-[15px]">{v.title}</span>
            <span className="block break-keep text-[#aebbd0] text-sm">{v.text}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** 운영사의 공인 기록 — 서비스가 아니라 회사의 것이라 캡션을 붙인다(특허는 검색 광고 특허다) */
function TrustList() {
  // 화이트라벨에는 운영사(비하이브) 기록을 싣지 않는다 — 대행사가 판매 주체다
  if (SHELL.credentials.length === 0) return null;
  return (
    <div className="hidden space-y-2 lg:block">
      <p className="font-semibold text-[#8093af] text-xs">운영사 {SHELL.company.name}</p>
      <ul className="flex flex-wrap gap-x-4 gap-y-1.5">
        {SHELL.credentials.map((c) => (
          <li key={c} className="inline-flex items-center gap-1.5 font-semibold text-[#b7c4d8] text-[13px]">
            <CheckIcon className="h-3.5 w-3.5 text-[#8fb4ff]" strokeWidth={3} />
            {c}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** 공개 화면의 흰 카드 */
export function AuthCard({
  title,
  description,
  icon,
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl bg-white p-6 shadow-[0_20px_40px_-12px_#0a37611f] ring-1 ring-slate-200/70 sm:p-8">
      {icon ? <div className="mb-4">{icon}</div> : null}
      {title ? <h2 className="font-extrabold text-2xl tracking-tight">{title}</h2> : null}
      {description ? <p className="mt-1.5 break-keep text-slate-500 text-sm leading-relaxed">{description}</p> : null}
      <div className={title || description ? 'mt-6' : ''}>{children}</div>
    </section>
  );
}

/** 결과 카드 머리의 원형 아이콘 */
export function StatusCircle({ tone, children }: { tone: 'ok' | 'wait' | 'bad'; children: ReactNode }) {
  const tones = {
    ok: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    wait: 'bg-blue-50 text-live ring-blue-100',
    bad: 'bg-red-50 text-red-600 ring-red-100',
  };
  return (
    <span className={`flex h-14 w-14 items-center justify-center rounded-full ring-8 ${tones[tone]}`}>{children}</span>
  );
}

/** 공개 화면 입력칸 — 공용 Input 기본값을 바꾸지 않고 여기서만 덮어쓴다 */
export const authField = 'h-11 rounded-lg border-slate-200 focus:border-live focus:ring-4 focus:ring-live/10';
